import React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CoverageLiabilityStatus } from '@/services/types/coverage-types';

interface CoverageLiabilityStatusBadgeProps {
  liabilityStatus: CoverageLiabilityStatus | null;
  denialReason?: string | null;
  showLabel?: boolean;
  className?: string;
}

export const CoverageLiabilityStatusBadge: React.FC<
  CoverageLiabilityStatusBadgeProps
> = ({ liabilityStatus, denialReason, showLabel = true, className = '' }) => {
  if (!liabilityStatus) {
    return null;
  }

  const statusConfig = {
    [CoverageLiabilityStatus.ACCEPTED]: {
      color: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-50',
      label: 'Accepted',
      shortLabel: 'Accepted',
    },
    [CoverageLiabilityStatus.REJECTED]: {
      color: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-50',
      label: 'Denied',
      shortLabel: 'Denied',
    },
    [CoverageLiabilityStatus.PARTIALLY_ACCEPTED]: {
      color:
        'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-50',
      label: 'Partially Accepted',
      shortLabel: 'Partial',
    },
    [CoverageLiabilityStatus.LIABILITY_UNKNOWN]: {
      color: 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-50',
      label: 'Unknown',
      shortLabel: 'Unknown',
    },
    [CoverageLiabilityStatus.UNDER_INVESTIGATION]: {
      color: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-50',
      label: 'Under Investigation',
      shortLabel: 'Investigating',
    },
  };

  const config = statusConfig[liabilityStatus];

  const badge = (
    <Badge
      variant="outline"
      className={`${config.color} ${className} px-2 py-0.5 text-xs font-medium`}
    >
      {showLabel ? config.label : config.shortLabel}
    </Badge>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-sm font-medium">Liability: {config.label}</p>
        {denialReason && (
          <p className="text-sm font-medium text-muted-foreground">
            {denialReason}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
